import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Image as ImageIcon, 
  Camera, 
  Microphone,
  X,
  Check,
  PaperPlaneRight,
  FileText,
  MapPin,
  User,
  PencilSimple,
  FolderOpen,
  Plus,
  Eye,
  EyeSlash
} from '@phosphor-icons/react';
import axios from 'axios';
import CameraCapture from './CameraCapture';
import VoiceRecorder from './VoiceRecorder';
import ImageEditor from './ImageEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Point 42 — compression used to only start inside handleSendAll, AFTER the
// user tapped Send. Moved to module scope so it can also be kicked off in
// the BACKGROUND the moment a preview is generated (file picked / edited),
// so by the time Send is actually tapped the compressed file is usually
// already sitting in memory and upload starts instantly.
// Session-48i (Bug B2): compress images before upload so buffering on slow
// cellular networks vanishes. Down-samples anything wider than 1600px to
// fit within 1600×1600 while preserving aspect ratio. JPEG quality 0.85
// keeps chat photos crisp but shrinks size ~60%.
const compressImage = (file) => new Promise((resolve) => {
  if (!file.type?.startsWith('image/') || file.type === 'image/gif') {
    resolve(file); return;
  }
  const img = new Image();
  const reader = new FileReader();
  reader.onload = (e) => { img.src = e.target.result; };
  reader.readAsDataURL(file);
  img.onload = () => {
    const MAX = 1600;
    let { width, height } = img;
    if (width <= MAX && height <= MAX && file.size < 300 * 1024) {
      resolve(file); return; // already small enough
    }
    if (width > height && width > MAX) { height = Math.round(height * (MAX / width)); width = MAX; }
    else if (height > MAX) { width = Math.round(width * (MAX / height)); height = MAX; }
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (blob) resolve(new File([blob], file.name || 'photo.jpg', { type: 'image/jpeg', lastModified: Date.now() }));
      else resolve(file);
    }, 'image/jpeg', 0.85);
  };
  img.onerror = () => resolve(file);
});

const MediaPicker = ({ onMediaSelected, onClose, userRole = 'customer', onOpenSavedTexts, onOpenSavedPhotos, initialFile = null }) => {
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const addMoreInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [viewOnce, setViewOnce] = useState(false); // point 63 — admin/handler only
  const [showCamera, setShowCamera] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  // Multi-media editor state
  const [mediaItems, setMediaItems] = useState(null); // [{file, caption, preview, isVideo, editedBlob}]
  const [activeIndex, setActiveIndex] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null); // Index of item being edited in ImageEditor

  // Shared group caption (for multiple items)
  const [groupCaption, setGroupCaption] = useState('');

  // Point 42 — kick off compression in the background as soon as an item's
  // preview exists (file picked, or re-edited), instead of waiting for Send.
  // Matched back to the item by `id` (stable per item, survives array
  // reordering) so a slow compression can't clobber a newer edit.
  const _mkItem = (file) => ({
    id: `mi-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    file,
    caption: '',
    preview: URL.createObjectURL(file),
    isVideo: file.type.startsWith('video/'),
    editedBlob: null,
    compressedFile: null,
  });
  const _kickOffCompression = (id, file, isVideo) => {
    if (isVideo) return; // only images are compressed client-side
    compressImage(file).then((compressed) => {
      setMediaItems(prev => prev
        ? prev.map(it => (it.id === id ? { ...it, compressedFile: compressed } : it))
        : prev);
    }).catch(() => {});
  };

  // Feature 17: Screenshot paste — if an initialFile is provided (e.g., from Ctrl+V paste), seed the editor
  useEffect(() => {
    if (initialFile && !mediaItems) {
      const item = _mkItem(initialFile);
      setMediaItems([item]);
      setActiveIndex(0);
      _kickOffCompression(item.id, item.file, item.isVideo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile]);

  // Select files → always go to multi-media editor
  // Session-53 — per-file size cap (10 MB for photos/videos, matches backend
  // SIZE_CAPS). Rejected files show a friendly toast BEFORE the request
  // even leaves the browser so the user isn't confused by a silent failure.
  const MAX_MEDIA_SIZE = 10 * 1024 * 1024;
  const _formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  const _filterOversizedFiles = (files) => {
    const ok = [];
    const rejected = [];
    for (const f of files) {
      if (f.size > MAX_MEDIA_SIZE) rejected.push(f);
      else ok.push(f);
    }
    if (rejected.length) {
      const names = rejected.map(f => `${f.name} (${_formatSize(f.size)})`).join(', ');
      toast.error(`File is too big — max 10 MB. Skipped: ${names}`, { duration: 6000 });
    }
    return ok;
  };

  const handleImageSelect = (e) => {
    const files = _filterOversizedFiles(Array.from(e.target.files).slice(0, 10));
    if (!files.length) return;

    const items = files.map(_mkItem);

    setMediaItems(items);
    setActiveIndex(0);
    setGroupCaption('');
    items.forEach(item => _kickOffCompression(item.id, item.file, item.isVideo));
  };

  // Add more files (from + button)
  const handleAddMore = (e) => {
    const files = _filterOversizedFiles(Array.from(e.target.files));
    if (!files.length || !mediaItems) return;

    const remaining = 10 - mediaItems.length;
    if (remaining <= 0) {
      toast.error('Maximum 10 files allowed');
      return;
    }

    const newItems = files.slice(0, remaining).map(_mkItem);

    setMediaItems(prev => [...prev, ...newItems]);
    // Switch to first new item
    setActiveIndex(mediaItems.length);
    newItems.forEach(item => _kickOffCompression(item.id, item.file, item.isVideo));
  };

  // When ImageEditor saves an edited image
  const handleItemEditSave = (editedBlob, captionText) => {
    const idx = editingIndex;
    const newPreview = URL.createObjectURL(editedBlob);
    let editedItemId = null;

    setMediaItems(prev => prev.map((item, i) => {
      if (i === idx) {
        // Revoke old preview if it was an edited blob preview
        if (item.editedBlob) URL.revokeObjectURL(item.preview);
        editedItemId = item.id;
        return {
          ...item,
          editedBlob,
          preview: newPreview,
          caption: captionText || item.caption,
          compressedFile: null, // Point 42 — stale, pixels changed; recompress below
        };
      }
      return item;
    }));
    // Point 42 — edited pixels invalidate any earlier background compression;
    // start a fresh one on the new blob right away, not at Send time.
    if (editedItemId) {
      _kickOffCompression(editedItemId, new File([editedBlob], 'edited.jpg', { type: editedBlob.type || 'image/jpeg' }), false);
    }
    setEditingIndex(null);
  };

  // Send all media items
  const handleSendAll = async () => {
    if (!mediaItems?.length) return;
    setIsUploading(true);
    try {
      const isMultiple = mediaItems.length > 1;
      for (let i = 0; i < mediaItems.length; i++) {
        const item = mediaItems[i];
        const formData = new FormData();
        const rawFile = item.editedBlob
          ? new File([item.editedBlob], item.file.name || 'edited.jpg', { type: item.editedBlob.type || 'image/jpeg' })
          : item.file;
        // Point 42 — compression was already kicked off in the background
        // when this item's preview was created/edited, so it's usually
        // ready here already (instant). Only fall back to compressing
        // synchronously now if the user hit Send before it finished.
        const fileToUpload = item.isVideo
          ? rawFile
          : (item.compressedFile || await compressImage(rawFile));
        formData.append('file', fileToUpload);
        
        // For multiple photos: caption only on LAST photo (WhatsApp style)
        // For single photo: use individual caption
        const caption = isMultiple 
          ? (i === mediaItems.length - 1 ? groupCaption : '') 
          : item.caption;
        
        // Session-48i (Bug B1): stamp a stable client_message_id so the
        // parent chat's socket echo dedupe finds & replaces the optimistic
        // bubble instead of adding a 2nd copy.
        const clientMessageId = `cmid-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`;
        
        if (item.isVideo) {
          formData.append('media_type', 'video');
          const response = await axios.post(`${API}/upload/media`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          // Session-48j (Photo broken fix): validate URL before proceeding.
          // Sometimes /upload/media silently returns 200 with empty url on
          // disk-full / permission issues → broken bubble saved to DB.
          if (!response.data?.url) throw new Error('Upload returned empty URL');
          onMediaSelected({ type: 'video', url: response.data.url, caption, client_message_id: clientMessageId, view_once: viewOnce });
        } else {
          formData.append('media_type', 'image');
          const response = await axios.post(`${API}/upload/media`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          if (!response.data?.url) throw new Error('Upload returned empty URL');
          onMediaSelected({ type: 'image', url: response.data.url, caption, file: fileToUpload, client_message_id: clientMessageId, view_once: viewOnce });
        }
      }
      // Cleanup previews
      mediaItems.forEach(item => URL.revokeObjectURL(item.preview));
      setMediaItems(null);
      setActiveIndex(0);
      setGroupCaption('');
      onClose();
    } catch (err) {
      // Session-48j: surface the actual reason so user knows why upload
      // failed instead of the previous silent "Failed to send media" toast.
      const msg = err?.response?.data?.detail || err?.message || 'Upload failed';
      toast.error(`Photo upload failed: ${msg}`);
    }
    setIsUploading(false);
  };

  // Remove item from list
  const removeItem = (idx) => {
    if (!mediaItems) return;
    URL.revokeObjectURL(mediaItems[idx].preview);
    const newItems = mediaItems.filter((_, i) => i !== idx);
    if (newItems.length === 0) {
      setMediaItems(null);
      setActiveIndex(0);
      return;
    }
    setMediaItems(newItems);
    if (activeIndex >= newItems.length) {
      setActiveIndex(newItems.length - 1);
    } else if (activeIndex > idx) {
      setActiveIndex(prev => prev - 1);
    }
  };

  // Close multi-media editor
  const closeMediaEditor = () => {
    if (mediaItems) {
      mediaItems.forEach(item => URL.revokeObjectURL(item.preview));
    }
    setMediaItems(null);
    setActiveIndex(0);
    setEditingIndex(null);
  };

  // Handle document/file selection
  const handleDocumentSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Session-53 — 10 MB cap (matches backend). Block before the request.
    if (file.size > MAX_MEDIA_SIZE) {
      toast.error(`File is too big — ${_formatSize(file.size)}. Max 10 MB allowed.`, { duration: 6000 });
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onMediaSelected({
        type: 'document',
        url: response.data.url,
        fileName: response.data.original_name || file.name,
        fileSize: response.data.size || file.size,
        extension: response.data.extension
      });
      onClose();
    } catch (error) {
      console.error('Document upload failed:', error);
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  // Camera photo → add to media items
  const handleCameraPhoto = (media) => {
    const file = new File([media.blob], 'camera_photo.jpg', { type: 'image/jpeg' });
    const item = _mkItem(file);
    setMediaItems([item]);
    setActiveIndex(0);
    setShowCamera(false);
    _kickOffCompression(item.id, item.file, item.isVideo);
  };

  // ===== RENDER SECTIONS =====

  // 1) If editing a specific image in ImageEditor
  if (editingIndex !== null && mediaItems?.[editingIndex]) {
    const item = mediaItems[editingIndex];
    const fileForEditor = item.editedBlob 
      ? new File([item.editedBlob], 'edit.jpg', { type: 'image/jpeg' })
      : item.file;
    
    return (
      <ImageEditor
        imageFile={fileForEditor}
        onSave={handleItemEditSave}
        onCancel={() => setEditingIndex(null)}
      />
    );
  }

  // 2) Multi-media editor (WhatsApp style)
  if (mediaItems?.length) {
    const activeItem = mediaItems[activeIndex] || mediaItems[0];
    
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        {/* Header */}
        <div className="bg-[#1f2c34] text-white px-3 py-2.5 flex items-center justify-between flex-shrink-0">
          <button 
            onClick={closeMediaEditor} 
            className="p-1.5 hover:bg-white/10 rounded-full"
            data-testid="media-editor-close"
          >
            <X size={24} weight="bold" />
          </button>
          <span className="text-sm font-medium opacity-70">
            {activeIndex + 1} / {mediaItems.length}
          </span>
          {!activeItem.isVideo && (
            <button 
              onClick={() => setEditingIndex(activeIndex)} 
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00a884] rounded-full text-sm font-semibold"
              data-testid="media-edit-btn"
            >
              <PencilSimple size={16} weight="bold" />
              Edit
            </button>
          )}
          {activeItem.isVideo && <div className="w-16" />}
        </div>
        
        {/* Main preview area */}
        <div className="flex-1 flex items-center justify-center p-2 overflow-hidden min-h-0">
          {activeItem.isVideo ? (
            <video 
              src={activeItem.preview} 
              className="max-w-full max-h-full object-contain rounded-lg" 
              controls 
              preload="metadata"
            />
          ) : (
            <img 
              src={activeItem.preview} 
              alt="" 
              className="max-w-full max-h-full object-contain rounded-lg" 
            />
          )}
        </div>
        
        {/* Bottom section */}
        <div className="bg-[#1f2c34] flex-shrink-0">
          {/* Thumbnail strip */}
          <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto" data-testid="media-thumbnail-strip">
            {mediaItems.map((item, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`relative w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                  idx === activeIndex ? 'border-[#00a884] scale-105' : 'border-transparent opacity-60'
                }`}
                data-testid={`media-thumb-${idx}`}
              >
                {item.isVideo ? (
                  <video src={item.preview} className="w-full h-full object-cover" preload="metadata" />
                ) : (
                  <img src={item.preview} alt="" className="w-full h-full object-cover" />
                )}
                {/* Remove button */}
                {mediaItems.length > 1 && (
                  <div
                    onClick={(e) => { e.stopPropagation(); removeItem(idx); }}
                    className="absolute top-0 right-0 w-5 h-5 bg-black/80 rounded-bl-lg flex items-center justify-center"
                  >
                    <X size={10} className="text-white" weight="bold" />
                  </div>
                )}
                {/* Edited indicator */}
                {item.editedBlob && (
                  <div className="absolute bottom-0 left-0 right-0 bg-[#00a884]/80 text-[8px] text-white text-center py-0.5">
                    Edited
                  </div>
                )}
              </button>
            ))}
            
            {/* Add more (+) button */}
            {mediaItems.length < 10 && (
              <button
                onClick={() => addMoreInputRef.current?.click()}
                className="w-14 h-14 flex-shrink-0 rounded-lg border-2 border-dashed border-gray-500 flex items-center justify-center hover:border-[#00a884] transition-colors"
                data-testid="media-add-more-btn"
              >
                <Plus size={22} className="text-gray-400" />
              </button>
            )}
          </div>
          
          {/* Caption + Send */}
          <div className="px-3 pb-3 flex items-center gap-3">
            <div className="flex-1 bg-[#2a3942] rounded-full px-4 py-2">
              {mediaItems.length > 1 ? (
                <input
                  type="text"
                  autoFocus
                  value={groupCaption}
                  onChange={(e) => setGroupCaption(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!isUploading) handleSendAll(); } }}
                  placeholder="Add a caption for all photos... (Enter to send)"
                  className="w-full bg-transparent text-white placeholder-gray-400 text-sm focus:outline-none"
                  data-testid="media-group-caption-input"
                />
              ) : (
                <input
                  type="text"
                  autoFocus
                  value={activeItem.caption}
                  onChange={(e) => {
                    const idx = activeIndex;
                    setMediaItems(prev => prev.map((f, i) => i === idx ? { ...f, caption: e.target.value } : f));
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!isUploading) handleSendAll(); } }}
                  placeholder="Add a caption... (Enter to send)"
                  className="w-full bg-transparent text-white placeholder-gray-400 text-sm focus:outline-none"
                  data-testid="media-caption-input"
                />
              )}
            </div>
            {/* Point 63 — view-once toggle, admin/handler senders only */}
            {userRole !== 'customer' && (
              <button
                onClick={() => setViewOnce(v => !v)}
                title={viewOnce ? 'View once: ON — recipient can open this only once' : 'Send as view-once'}
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${viewOnce ? 'bg-blue-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
                data-testid="media-view-once-toggle"
              >
                {viewOnce ? <Eye size={20} weight="fill" /> : <EyeSlash size={20} />}
              </button>
            )}
            <button
              onClick={handleSendAll}
              disabled={isUploading}
              className="w-12 h-12 bg-[#00a884] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg disabled:opacity-50"
              data-testid="media-send-btn"
              data-kb-submit
            >
              {isUploading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <PaperPlaneRight size={24} className="text-white" weight="fill" />
              )}
            </button>
          </div>
        </div>
        
        {/* Hidden input for add more */}
        <input
          ref={addMoreInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleAddMore}
          className="hidden"
        />
      </div>
    );
  }

  // 3) Camera Capture
  if (showCamera) {
    return (
      <CameraCapture
        onPhotoTaken={handleCameraPhoto}
        onClose={() => setShowCamera(false)}
      />
    );
  }

  // 4) Voice Recorder
  if (showVoiceRecorder) {
    return (
      <VoiceRecorder
        onVoiceSent={(media) => {
          onMediaSelected(media);
          setShowVoiceRecorder(false);
        }}
        onClose={() => setShowVoiceRecorder(false)}
      />
    );
  }

  // 5) Contact Form
  if (showContactForm) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-sm">
          <div className="bg-[#008069] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
            <User size={20} weight="fill" />
            <span className="font-semibold">Share Contact</span>
          </div>
          <div className="p-4 space-y-3">
            <input
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Contact Name"
              className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#008069]"
              autoFocus
              data-testid="contact-name-input"
            />
            <input
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="Phone Number"
              type="tel"
              className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#008069]"
              data-testid="contact-phone-input"
            />
            <div className="flex gap-2 pt-2">
              <button onClick={() => { setShowContactForm(false); setContactName(''); setContactPhone(''); }} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button 
                onClick={() => {
                  if (!contactName.trim() || !contactPhone.trim()) { toast.error('Enter name and phone'); return; }
                  onMediaSelected({ type: 'text', text: `👤 ${contactName.trim()}\n📞 ${contactPhone.trim()}` });
                  setShowContactForm(false);
                  setContactName('');
                  setContactPhone('');
                  onClose();
                }}
                className="flex-1 py-2 bg-[#008069] text-white rounded-lg text-sm hover:bg-[#006e5a]"
                data-testid="send-contact-btn"
              >Send</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 6) Main attachment grid (Bottom sheet)
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div 
        className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="bg-white w-full max-w-md rounded-t-2xl pb-6 pt-3 animate-slideUp" onClick={(e) => e.stopPropagation()}>
          {/* Handle bar */}
          <div className="flex justify-center mb-4">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>

          <div className="px-6 grid grid-cols-3 gap-y-6 gap-x-4">
            {/* Document */}
            <button
              onClick={() => documentInputRef.current?.click()}
              disabled={isUploading}
              className="flex flex-col items-center gap-2"
              data-testid="attach-document"
            >
              <div className="w-14 h-14 rounded-full bg-[#7f66ff] flex items-center justify-center shadow-md">
                <FileText size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-gray-700 font-medium">Document</span>
            </button>

            {/* Camera */}
            <button
              onClick={() => setShowCamera(true)}
              className="flex flex-col items-center gap-2"
              data-testid="attach-camera"
            >
              <div className="w-14 h-14 rounded-full bg-[#ff2e74] flex items-center justify-center shadow-md">
                <Camera size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-gray-700 font-medium">Camera</span>
            </button>

            {/* Gallery */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center gap-2"
              data-testid="attach-gallery"
            >
              <div className="w-14 h-14 rounded-full bg-[#bf59cf] flex items-center justify-center shadow-md">
                <ImageIcon size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-gray-700 font-medium">Gallery</span>
            </button>

            {/* Audio */}
            <button
              onClick={() => setShowVoiceRecorder(true)}
              className="flex flex-col items-center gap-2"
              data-testid="attach-audio"
            >
              <div className="w-14 h-14 rounded-full bg-[#ee7b30] flex items-center justify-center shadow-md">
                <Microphone size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-gray-700 font-medium">Audio</span>
            </button>

            {/* Location */}
            <button
              onClick={async () => {
                try {
                  if (!navigator.geolocation) {
                    toast.error('Location not supported');
                    return;
                  }
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      const { latitude, longitude } = pos.coords;
                      const mapUrl = `https://maps.google.com/maps?q=${latitude},${longitude}`;
                      onMediaSelected({ type: 'text', text: mapUrl });
                      onClose();
                    },
                    () => { toast.error('Location access denied'); }
                  );
                } catch (e) { toast.error('Failed to get location'); }
              }}
              className="flex flex-col items-center gap-2"
              data-testid="attach-location"
            >
              <div className="w-14 h-14 rounded-full bg-[#1fa855] flex items-center justify-center shadow-md">
                <MapPin size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-gray-700 font-medium">Location</span>
            </button>

            {/* Contact */}
            <button
              onClick={async () => {
                if ('contacts' in navigator && 'ContactsManager' in window) {
                  try {
                    const contacts = await navigator.contacts.select(['name', 'tel'], { multiple: false });
                    if (contacts?.length) {
                      const c = contacts[0];
                      const name = c.name?.[0] || 'Unknown';
                      const phone = c.tel?.[0] || '';
                      onMediaSelected({ type: 'text', text: `👤 ${name}\n📞 ${phone}` });
                      onClose();
                    }
                  } catch(e) {
                    setShowContactForm(true);
                  }
                } else {
                  setShowContactForm(true);
                }
              }}
              className="flex flex-col items-center gap-2"
              data-testid="attach-contact"
            >
              <div className="w-14 h-14 rounded-full bg-[#009de2] flex items-center justify-center shadow-md">
                <User size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-gray-700 font-medium">Contact</span>
            </button>

            {/* Saved Texts - Handler/Admin only */}
            {(userRole === 'handler' || userRole === 'admin') && (
              <button
                onClick={() => {
                  onClose();
                  if (onOpenSavedTexts) {
                    setTimeout(() => onOpenSavedTexts(), 100);
                  }
                }}
                className="flex flex-col items-center gap-2"
                data-testid="attach-saved-texts"
              >
                <div className="w-14 h-14 rounded-full bg-[#5856d6] flex items-center justify-center shadow-md">
                  <FileText size={26} className="text-white" weight="fill" />
                </div>
                <span className="text-xs text-gray-700 font-medium">Saved Texts</span>
              </button>
            )}

            {/* Saved Photos - Handler/Admin only */}
            {(userRole === 'handler' || userRole === 'admin') && (
              <button
                onClick={() => {
                  onClose();
                  if (onOpenSavedPhotos) {
                    setTimeout(() => onOpenSavedPhotos(), 100);
                  }
                }}
                className="flex flex-col items-center gap-2"
                data-testid="attach-saved-photos"
              >
                <div className="w-14 h-14 rounded-full bg-[#ff9500] flex items-center justify-center shadow-md">
                  <FolderOpen size={26} className="text-white" weight="fill" />
                </div>
                <span className="text-xs text-gray-700 font-medium">Saved Photos</span>
              </button>
            )}
          </div>

          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleImageSelect}
            className="hidden"
          />
          <input
            ref={documentInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
            onChange={handleDocumentSelect}
            className="hidden"
          />

          {isUploading && (
            <div className="flex justify-center mt-4">
              <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <style>{`
          @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
          .animate-slideUp { animation: slideUp 0.25s ease-out; }
        `}</style>
      </div>
    </div>
  );
};

export default MediaPicker;
